/** Marketing i18n fragment — ucApprovalLedger (use-cases/ai-agent-approval-ledger). */

export const ucApprovalLedgerEn = {
  shot: {
    label: "Mission control & approvals",
    alt: "The AI mission control center — the agent delegation tree, approval count and a live activity log of every decision and gate.",
  },
  meta: {
    title: "AI approval ledger — every decision on the record",
    description:
      "FixControl records each AI-assisted action as a decision: who approved it, the evidence they saw, and what happened next. Review any action after the fact, or export the ledger for audit.",
  },
  hero: {
    eyebrow: "Governed AI",
    title: "A decision ledger for",
    highlight: "every AI-assisted action",
    subtitle:
      "When an auditor, a customer of your organization or an incident review asks “who approved this, and why?”, the answer should take minutes, not days. When AI proposes a reply, a patch or a deployment promotion, someone on your team approves it — and the decision goes on the record with the evidence behind it.",
    primaryCta: "Book a demo",
    secondaryCta: "Talk to us",
    bullet1: "Attributable approvals",
    bullet2: "Evidence kept with the verdict",
    bullet3: "Audit-ready export",
  },
  how: {
    eyebrow: "How it works",
    title: "From proposal to recorded decision",
    description:
      "AI proposes; someone on your team decides; the ledger keeps both.",
  },
  steps: {
    proposal: {
      tag: "Proposal",
      title: "AI proposes an action, with evidence",
      body: "A reply, a patch or a deployment promotion arrives as a proposal. The evidence that justifies it is laid out for someone on your team to read before anything happens.",
    },
    surface: {
      tag: "Decision",
      title: "Someone on your team decides where they already work",
      body: "Approvers act from Slack or Teams approval cards, or in the FixControl app. The verdict is captured at the moment it is made, along with the channel it came from.",
    },
    ledger: {
      tag: "Ledger entry",
      title: "The decision becomes a ledger entry",
      body: "On a verdict, FixControl writes the entry: the approver and their role, the exact text, diff or action approved, and the evidence in front of them at that moment.",
    },
    consequence: {
      tag: "Consequence",
      title: "What happened next is recorded too",
      body: "The outcome of the approved action lands in the same entry. Months later, you can see not only that a change was approved but what it caused.",
    },
  },
  features: {
    eyebrow: "What the ledger captures",
    title: "Six questions every entry can answer",
    description:
      "Each entry is built to answer what an auditor or an incident review will actually ask.",
    actor: {
      title: "Who approved, and in which role",
      body: "Every entry names a person and the role they acted under. When someone asks who signed off on a change, the ledger has a name and a role.",
    },
    channel: {
      title: "Where the decision came from",
      body: "Slack, Teams or in-app: the ledger records which channel each verdict arrived through. Provenance is part of the record.",
    },
    evidence: {
      title: "What the approver saw",
      body: "The evidence presented at the moment of decision is stored with the verdict. A later reviewer reads exactly what the approver read.",
    },
    approved: {
      title: "What was approved, verbatim",
      body: "The entry captures the final reply text, the code diff or the action itself. There is no summarizing step between what a person approved and what the ledger says they approved.",
    },
    consequence: {
      title: "What followed",
      body: "The outcome of the action is recorded alongside the decision. The ledger closes the loop between an approval and its effect.",
    },
    timeline: {
      title: "A timeline people can read",
      body: "An operational timeline tells the story of a mission in plain terms, kept separate from raw agent traces. Reviewers get a readable account, and the traces stay available for debugging.",
    },
  },
  governed: {
    eyebrow: "Audit readiness",
    title: "Built to be reviewed after the fact",
    noteTitle: "What an entry looks like",
    noteBody:
      "A single entry reads like a report — a fictional but representative example: customer reply — sent; approved by a support lead on your own team, from Slack, at 14:02, after reviewing the final text and the ticket history. By default, nothing ships without a recorded human decision; any autonomy is explicit, opt-in, policy-bounded and audit-logged. The ledger is structured for export, so an auditor can follow any action back to the person and evidence behind it.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions auditors and security teams ask",
    q1: {
      q: "How is this different from logging what an agent did?",
      a: "Logs record activity after the fact. The ledger records decisions: a person approved a specific action, on specific evidence, at a specific moment. You audit choices, not just events.",
    },
    q2: {
      q: "What exactly is recorded for each decision?",
      a: "Who approved, the role they held, the channel they approved from, the timestamp, the evidence shown, the exact text, diff or action approved, and the consequence that followed.",
    },
    q3: {
      q: "Where do people approve?",
      a: "In Slack and Teams approval cards, or in the FixControl app. The channel is part of each entry, so you can tell whether a verdict came from a chat card or the console.",
    },
    q4: {
      q: "Why keep a timeline separate from agent logs?",
      a: "Raw traces are written for debugging. The timeline is written for the people reviewing the work: who did what, in what order, and why. An audit should not require reconstructing a story from log lines.",
    },
    q5: {
      q: "Can the ledger be exported for audit?",
      a: "Yes. The ledger is structured for export and after-the-fact review, and each entry carries enough context to stand on its own.",
    },
    q6: {
      q: "Does any action ever skip approval?",
      a: "By default, nothing ships without a recorded human decision. Any autonomy — such as the policy-scoped low-risk email auto-send mode — is explicit, opt-in, policy-bounded and audit-logged, and every send still lands in the ledger.",
    },
  },
  related: {
    approvedReplies: {
      label: "Approved replies",
      description: "Draft with AI, approve the exact text that gets sent.",
    },
    deployment: {
      label: "Deployment governance",
      description: "Hold promotions at a gate until a person approves.",
    },
    slack: {
      label: "Slack integration",
      description: "Approval cards and decision capture in the thread.",
    },
  },
  cta: {
    title: "See a ledger entry end to end",
    description:
      "Book a demo: approve a real action, then open the entry it produced — evidence, verdict and consequence.",
    primary: "Book a demo",
    secondary: "Talk to us",
  },
};

export const ucApprovalLedgerNl = {
  shot: {
    label: "Missiecontrole & goedkeuringen",
    alt: "Het AI-missiecontrolecentrum — de agent-delegatieboom, het aantal goedkeuringen en een live activiteitenlog van elke beslissing en poort.",
  },
  meta: {
    title: "AI-goedkeuringsgrootboek — elke beslissing vastgelegd",
    description:
      "FixControl legt elke AI-ondersteunde actie vast als een beslissing: wie het goedkeurde, het bewijs dat hij zag, en wat er daarna gebeurde. Bekijk elke actie achteraf, of exporteer het grootboek voor audit.",
  },
  hero: {
    eyebrow: "Gegoverneerde AI",
    title: "Een beslissingsgrootboek voor",
    highlight: "elke AI-ondersteunde actie",
    subtitle:
      "Als een auditor, een klant van je organisatie of een incidentreview vraagt “wie heeft dit goedgekeurd, en waarom?”, hoort het antwoord minuten te kosten, geen dagen. Wanneer AI een antwoord, een patch of een deployment-promotie voorstelt, keurt iemand van je team het goed — en de beslissing wordt vastgelegd met het bewijs erachter.",
    primaryCta: "Boek een demo",
    secondaryCta: "Neem contact op",
    bullet1: "Toerekenbare goedkeuringen",
    bullet2: "Bewijs bewaard bij het oordeel",
    bullet3: "Audit-klare export",
  },
  how: {
    eyebrow: "Hoe het werkt",
    title: "Van voorstel naar een vastgelegde beslissing",
    description:
      "AI stelt voor; iemand van je team beslist; het grootboek bewaart beide.",
  },
  steps: {
    proposal: {
      tag: "Voorstel",
      title: "AI stelt een actie voor met bewijs",
      body: "Een antwoord, een patch of een deployment-promotie komt binnen als een voorstel. Het bewijs dat het rechtvaardigt, wordt uitgelegd zodat iemand van je team het kan lezen voordat er iets gebeurt.",
    },
    surface: {
      tag: "Beslissing",
      title: "Iemand van je team beslist waar hij al werkt",
      body: "Goedkeurders handelen vanaf Slack- of Teams-goedkeuringskaarten, of in de FixControl-app. Het oordeel wordt vastgelegd op het moment dat het wordt genomen, samen met het kanaal waar het vandaan kwam.",
    },
    ledger: {
      tag: "Grootboekregel",
      title: "De beslissing wordt een grootboekregel",
      body: "Bij een oordeel schrijft FixControl de regel: de goedkeurder en zijn rol, de exacte tekst, diff of actie die werd goedgekeurd, en het bewijs dat hij op dat moment voor zich had.",
    },
    consequence: {
      tag: "Gevolg",
      title: "Wat er daarna gebeurde, wordt ook vastgelegd",
      body: "De uitkomst van de goedgekeurde actie belandt in dezelfde regel. Maanden later zie je niet alleen dat een wijziging werd goedgekeurd, maar ook wat ze veroorzaakte.",
    },
  },
  features: {
    eyebrow: "Wat het grootboek vastlegt",
    title: "Zes vragen die elke regel kan beantwoorden",
    description:
      "Elke regel is gebouwd om te beantwoorden wat een auditor of een incidentreview daadwerkelijk zal vragen.",
    actor: {
      title: "Wie goedkeurde, en welke rol",
      body: "Elke regel benoemt een persoon en de rol waaronder hij handelde. Vraagt iemand wie een wijziging heeft afgetekend, dan heeft het grootboek een naam en een rol.",
    },
    channel: {
      title: "Vanuit welk kanaal de beslissing kwam",
      body: "Slack, Teams of in-app: het grootboek legt vast via welk kanaal elk oordeel binnenkwam. De herkomst is deel van de registratie.",
    },
    evidence: {
      title: "Wat de goedkeurder zag",
      body: "Het bewijs dat op het moment van beslissen werd getoond, wordt bij het oordeel bewaard. Een latere reviewer leest precies wat de goedkeurder las.",
    },
    approved: {
      title: "Wat werd goedgekeurd, letterlijk",
      body: "De regel legt de definitieve antwoordtekst, de code-diff of de actie zelf vast. Er zit geen samenvattende stap tussen wat een mens goedkeurde en wat het grootboek zegt dat hij goedkeurde.",
    },
    consequence: {
      title: "Welk gevolg erop volgde",
      body: "De uitkomst van de actie wordt naast de beslissing vastgelegd. Het grootboek sluit de lus tussen een goedkeuring en het effect ervan.",
    },
    timeline: {
      title: "Een tijdlijn die mensen kunnen lezen",
      body: "Een operationele tijdlijn vertelt het verhaal van een missie in gewone taal, gescheiden gehouden van ruwe agent-traces. Reviewers krijgen een leesbaar verslag, en de traces blijven beschikbaar voor debuggen.",
    },
  },
  governed: {
    eyebrow: "Audit-gereedheid",
    title: "Gebouwd om achteraf te worden gereviewd",
    noteTitle: "Hoe een regel eruitziet",
    noteBody:
      "Eén regel leest als een rapport — hier een fictief maar representatief voorbeeld: klantantwoord — verzonden; goedgekeurd door een support-lead van je eigen team, vanuit Slack, om 14:02, na het bekijken van de definitieve tekst en de tickethistorie. Standaard gaat er niets uit zonder een vastgelegde menselijke beslissing; elke autonomie is expliciet, opt-in, policy-begrensd en audit-logged. Het grootboek is gestructureerd voor export, zodat een auditor elke actie kan terugvolgen naar de persoon en het bewijs erachter.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen die auditors en securityteams stellen",
    q1: {
      q: "Hoe verschilt dit van het loggen van wat een agent deed?",
      a: "Logs leggen activiteit achteraf vast. Het grootboek legt beslissingen vast: een mens keurde een specifieke actie goed, op specifiek bewijs, op een specifiek moment. Je auditeert keuzes, niet alleen gebeurtenissen.",
    },
    q2: {
      q: "Wat wordt er precies vastgelegd voor elke beslissing?",
      a: "Wie goedkeurde, welke rol hij had, vanuit welk kanaal hij goedkeurde, het tijdstip, welk bewijs werd getoond, de exacte tekst, diff of actie die werd goedgekeurd, en het gevolg dat erop volgde.",
    },
    q3: {
      q: "Waar keuren mensen goed?",
      a: "In Slack- en Teams-goedkeuringskaarten, of in de FixControl-app. Het kanaal maakt deel uit van elke regel, zodat je kunt zien of een oordeel van een chat-kaart of de console kwam.",
    },
    q4: {
      q: "Waarom een tijdlijn scheiden van agent-logs?",
      a: "Ruwe traces worden geschreven voor debuggen. De tijdlijn wordt geschreven voor de mensen die het werk reviewen: wie wat deed, in welke volgorde, en waarom. Een audit zou geen verhaal uit logregels moeten hoeven reconstrueren.",
    },
    q5: {
      q: "Kan het grootboek voor audit worden geëxporteerd?",
      a: "Ja. Het grootboek is gestructureerd voor export en review achteraf, en elke regel draagt genoeg context om op zichzelf te staan.",
    },
    q6: {
      q: "Slaat een actie ooit goedkeuring over?",
      a: "Standaard gaat er niets uit zonder een vastgelegde menselijke beslissing. Elke autonomie — zoals de policy-begrensde laag-risico e-mail-auto-send-modus — is expliciet, opt-in, policy-begrensd en audit-logged, en elke verzending belandt nog steeds in het grootboek.",
    },
  },
  related: {
    approvedReplies: {
      label: "Goedgekeurde antwoorden",
      description: "Stel op met AI, keur precies goed wat er verstuurd wordt.",
    },
    deployment: {
      label: "Deployment-governance",
      description: "Houd promoties bij een poort tot een mens goedkeurt.",
    },
    slack: {
      label: "Slack-integratie",
      description: "Goedkeuringskaarten en beslissingsvastlegging in de thread.",
    },
  },
  cta: {
    title: "Zie een grootboekregel van begin tot eind",
    description:
      "Boek een demo: keur een echte actie goed, open dan de regel die eruit voortkwam — bewijs, oordeel en gevolg.",
    primary: "Boek een demo",
    secondary: "Neem contact op",
  },
};
